import React, { Fragment, useState } from "react";
import { styled } from "@mui/material/styles";
import { useRouter } from "next/router";
import { Chip, Typography } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";

import AnimateWrapper from "@common/animations/AnimateWrapper";
import Navbar from "@common/Navbar";
import ImageCarousel from "../ImageCarousel";
import ProductReleaseCountdown from "./components/ProductReleaseCountdown";

import useShopContext from "@hooks/useShopContext";
import useInterval from "@hooks/useInterval";
import OutlinedButton from "@common/OutlinedButton";
import { isDark } from "@common/utils/colors";
import { checkProductAvailability, getPercentageDiscount } from "@common/utils/productUtils";
import { hasVariantOnDiscount } from "@common/utils/productListUtils";
import { prefixCurrency } from "@common/utils/currency";

const PREFIX = "ProductDescriptionContainer";

const classes = {
  imageDiv: `${PREFIX}-imageDiv`,
  productDescriptionDiv: `${PREFIX}-productDescriptionDiv`,
  inventory: `${PREFIX}-inventory`,
  buttonDiv: `${PREFIX}-buttonDiv`,
  discountLabel: `${PREFIX}-discountLabel`,
};

const StyledAnimateWrapper = styled(AnimateWrapper)(({ theme }) => ({
  display: "grid",
  placeItems: "center",
  gridTemplateColumns: "1fr 1fr",
  margin: "auto",
  maxWidth: 1280,
  padding: theme.spacing(5, 0),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    alignContent: "start",
    padding: 0,
    margin: "0 0 auto",
  },

  [`& .${classes.imageDiv}`]: {
    width: 550,
    height: 620,
    alignSelf: "flex-start",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      height: "auto",
      marginBottom: theme.spacing(4),
    },
  },

  [`& .${classes.productDescriptionDiv}`]: {
    display: "flex",
    flexDirection: "column",
    width: "80%",
    color: theme.palette.primary.contrastText,
  },

  [`& .${classes.inventory}`]: {
    color: isDark(theme.palette.primary.main) ? theme.palette.grey[400] : theme.palette.grey[600],
  },

  [`& .${classes.buttonDiv}`]: {
    marginTop: theme.spacing(7),
    [theme.breakpoints.down("md")]: {
      margin: `${theme.spacing(3)} 0`,
    },
  },

  [`& .${classes.discountLabel}`]: {
    height: "24px",
    backgroundColor: theme.palette.common.white,
    color: theme.palette.grey[900],
    border: `1px solid ${theme.palette.secondary.main}`,
    fontSize: "0.85rem",
    "& .MuiChip-label": {
      padding: "0px 8px",
    },
  },
}));

const ProductDescriptionContainer = ({ shop, product, onClickFn }) => {
  const router = useRouter();
  const { vendor } = useShopContext();

  const soldout = product && product.sold_out;
  const shopClosed = shop && !shop.is_active;

  const [productAvailable, setProductAvailable] = useState(
    checkProductAvailability(product && product),
  ); // for time-based product

  useInterval(() => {
    const updatedAvailability = checkProductAvailability(product && product);
    if (updatedAvailability !== productAvailable) {
      setProductAvailable(updatedAvailability);
    }
  }, 1000);

  const getLowestPrice = () => {
    const minPrice = product?.variants.reduce((a, b) => ({
      price: Math.min(parseFloat(a.price), parseFloat(b.price)),
    }));
    return minPrice ? parseFloat(minPrice.price).toFixed(2) : "";
  };

  const showPricing = () => {
    if (product.use_variants) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {`From ${prefixCurrency(getLowestPrice(), vendor)}`}
          {hasVariantOnDiscount(product) && <Chip label="Sale" className={classes.discountLabel} />}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {prefixCurrency(product.price, vendor)}
        {product.original_price && (
          <span style={{ textDecoration: "line-through" }}>
            {prefixCurrency(product.original_price, vendor)}
          </span>
        )}
        {product.show_percentage_discount && (
          <Chip
            label={getPercentageDiscount(product.original_price, product.price) + `%`}
            className={classes.discountLabel}
          />
        )}
      </div>
    );
  };

  const renderButton = () => {
    if (shopClosed) {
      return (
        <OutlinedButton sx={{ float: "right" }} variant="outlined" size="small" disabled>
          {"Sorry, We're Closed"}
        </OutlinedButton>
      );
    }

    if (!productAvailable) {
      return (
        <OutlinedButton sx={{ float: "right" }} variant="outlined" size="small" disabled>
          Not Available
        </OutlinedButton>
      );
    }

    if (soldout) {
      return (
        <OutlinedButton sx={{ float: "right" }} variant="outlined" size="small" disabled>
          Sold Out
        </OutlinedButton>
      );
    }

    if (product?.use_variants) {
      return (
        <OutlinedButton
          sx={{ float: "right" }}
          variant="outlined"
          size="small"
          endIcon={<ArrowForward />}
          onClick={onClickFn}
        >
          Select Options
        </OutlinedButton>
      );
    }

    return (
      <OutlinedButton
        sx={{ float: "right" }}
        variant="outlined"
        size="small"
        endIcon={<ArrowForward />}
        onClick={onClickFn}
      >
        Continue
      </OutlinedButton>
    );
  };

  return (
    <Fragment>
      <Navbar onBack={() => router.push("/products")} progressBarOffSet />
      <StyledAnimateWrapper motionKey="ProductDescription">
        <div className={classes.imageDiv}>
          <ImageCarousel images={product?.images} showThumbs />
        </div>
        <div className={classes.productDescriptionDiv}>
          {product?.available_start_time && product?.available_end_time && (
            <ProductReleaseCountdown product={product} />
          )}
          <Typography variant="h1" style={{ fontWeight: 500 }}>
            {product?.title}
          </Typography>
          <Typography variant="h5" color="accentText.secondary">
            {product && showPricing()}
          </Typography>
          {product && !!product.inventory && (
            <Typography variant="body2" className={classes.inventory}>
              <i>{product.inventory} item(s) left</i>
            </Typography>
          )}
          <Typography variant="body1" style={{ paddingTop: "8px", whiteSpace: "pre-line" }}>
            {product?.description}
          </Typography>
          <div className={classes.buttonDiv}>{renderButton()}</div>
        </div>
      </StyledAnimateWrapper>
    </Fragment>
  );
};

export default ProductDescriptionContainer;
